import * as React from "react";
import MDEditor from "@uiw/react-md-editor";
import { Braces, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  PROMPT_PLACEHOLDERS,
  groupPlaceholdersByCategory,
  type PromptPlaceholder,
} from "@/types/prompt-placeholders";

// Textarea caret pixel position via the mirror-div technique.
const MIRROR_PROPS = [
  "boxSizing",
  "width",
  "height",
  "overflowX",
  "overflowY",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "fontStyle",
  "fontVariant",
  "fontWeight",
  "fontStretch",
  "fontSize",
  "lineHeight",
  "fontFamily",
  "textAlign",
  "textTransform",
  "textIndent",
  "letterSpacing",
  "wordSpacing",
  "whiteSpace",
  "wordWrap",
] as const;

function caretCoordinates(el: HTMLTextAreaElement, position: number) {
  const div = document.createElement("div");
  const computed = window.getComputedStyle(el);
  const style = div.style;
  style.position = "absolute";
  style.visibility = "hidden";
  style.whiteSpace = "pre-wrap";
  style.wordWrap = "break-word";
  for (const prop of MIRROR_PROPS) {
    style[prop as never] = computed[prop as never];
  }
  div.textContent = el.value.substring(0, position);
  const span = document.createElement("span");
  span.textContent = el.value.substring(position) || ".";
  div.appendChild(span);
  document.body.appendChild(div);
  const top = span.offsetTop + parseInt(computed.borderTopWidth || "0", 10);
  const left = span.offsetLeft + parseInt(computed.borderLeftWidth || "0", 10);
  const height = parseInt(computed.lineHeight || "16", 10);
  document.body.removeChild(div);
  return { top, left, height };
}

interface PromptEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: number;
}

/**
 * Markdown prompt editor with `{{…}}` placeholder autocomplete: typing
 * `{{` opens a caret-anchored, filter-as-you-type list of the resolver's
 * placeholders; ↑/↓ navigate, Enter/Tab insert `{{name}}`, Esc closes.
 */
export function PromptEditor({ value, onChange, height = 480 }: PromptEditorProps) {
  const { t } = useTranslation("templates");
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<PromptPlaceholder[]>([]);
  const [active, setActive] = React.useState(0);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  const triggerStart = React.useRef(0);

  // Categorised placeholder reference sidebar (toggled by a button).
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [sidebarFilter, setSidebarFilter] = React.useState("");

  const getTextarea = () =>
    wrapRef.current?.querySelector<HTMLTextAreaElement>("textarea") ?? null;

  const close = () => setOpen(false);

  // Insert `{{name}}` at the current caret (or append when unfocused). Used
  // by the sidebar; the autocomplete has its own `{{`-aware `insert`.
  const insertAtCaret = React.useCallback(
    (name: string) => {
      const text = `{{${name}}}`;
      const ta = getTextarea();
      if (!ta) {
        onChange((value ?? "") + text);
        return;
      }
      const start = ta.selectionStart ?? ta.value.length;
      const end = ta.selectionEnd ?? start;
      const before = ta.value.slice(0, start);
      const after = ta.value.slice(end);
      onChange(before + text + after);
      const next = before.length + text.length;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(next, next);
      });
    },
    [onChange, value]
  );

  const groups = React.useMemo(() => {
    const q = sidebarFilter.trim().toLowerCase();
    return groupPlaceholdersByCategory()
      .map((group) => ({
        ...group,
        items: q
          ? group.items.filter(
              (p) =>
                p.name.toLowerCase().includes(q) ||
                p.description.toLowerCase().includes(q)
            )
          : group.items,
      }))
      .filter((group) => group.items.length > 0);
  }, [sidebarFilter]);

  const refresh = React.useCallback(() => {
    const ta = getTextarea();
    if (!ta) return close();
    const caret = ta.selectionStart ?? 0;
    const before = ta.value.slice(0, caret);
    // `{{` optionally followed by a partial word (letters/digits/_), no `}}`.
    const match = /\{\{(\w*)$/.exec(before);
    if (!match) return close();
    const partial = match[1].toLowerCase();
    const filtered = PROMPT_PLACEHOLDERS.filter((p) =>
      p.name.toLowerCase().includes(partial)
    );
    if (filtered.length === 0) return close();

    triggerStart.current = caret - match[0].length; // index of the `{{`
    const c = caretCoordinates(ta, caret);
    setPos({
      top: ta.offsetTop + c.top - ta.scrollTop + c.height,
      left: ta.offsetLeft + c.left - ta.scrollLeft,
    });
    setItems(filtered);
    setActive(0);
    setOpen(true);
  }, []);

  const insert = React.useCallback(
    (item: PromptPlaceholder) => {
      const ta = getTextarea();
      if (!ta) return;
      const caret = ta.selectionStart ?? 0;
      const before = ta.value.slice(0, triggerStart.current);
      const after = ta.value.slice(caret);
      const text = `{{${item.name}}}`;
      onChange(before + text + after);
      close();
      const next = before.length + text.length;
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(next, next);
      });
    },
    [onChange]
  );

  // Attach native listeners to the MDEditor textarea.
  React.useEffect(() => {
    const ta = getTextarea();
    if (!ta) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(a + 1, items.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(a - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const item = items[active];
        if (item) insert(item);
      } else if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key))
        return;
      refresh();
    };
    const onClick = () => refresh();
    const onScroll = () => (open ? refresh() : undefined);
    const onBlur = () => window.setTimeout(close, 120);

    ta.addEventListener("keydown", onKeyDown);
    ta.addEventListener("keyup", onKeyUp);
    ta.addEventListener("click", onClick);
    ta.addEventListener("scroll", onScroll);
    ta.addEventListener("blur", onBlur);
    return () => {
      ta.removeEventListener("keydown", onKeyDown);
      ta.removeEventListener("keyup", onKeyUp);
      ta.removeEventListener("click", onClick);
      ta.removeEventListener("scroll", onScroll);
      ta.removeEventListener("blur", onBlur);
    };
  }, [open, items, active, insert, refresh]);

  return (
    <div className="space-y-2" data-color-mode="light">
      <div className="flex justify-end">
        <Button
          type="button"
          variant={sidebarOpen ? "secondary" : "outline"}
          size="sm"
          onClick={() => setSidebarOpen((o) => !o)}
          aria-pressed={sidebarOpen}
        >
          <Braces className="mr-1.5 h-4 w-4" />
          {t("promptEditor.placeholders")}
        </Button>
      </div>

      <div className="flex gap-3">
        <div ref={wrapRef} className="relative min-w-0 flex-1">
          <MDEditor
            value={value}
            onChange={(v) => onChange(v ?? "")}
            height={height}
            preview="edit"
          />
          {open && items.length > 0 && (
            <ul
              className="absolute z-50 max-h-64 w-80 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
              style={{ top: pos.top, left: pos.left }}
            >
              {items.map((item, i) => (
                <li key={item.name}>
                  <button
                    type="button"
                    // onMouseDown (not click) so it fires before the textarea blur.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      insert(item);
                    }}
                    onMouseEnter={() => setActive(i)}
                    className={cn(
                      "flex w-full flex-col rounded px-2 py-1.5 text-left",
                      i === active ? "bg-accent" : "hover:bg-muted"
                    )}
                  >
                    <span className="font-mono text-xs">{`{{${item.name}}}`}</span>
                    <span className="text-xs text-muted-foreground">
                      {item.description}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {sidebarOpen && (
          <aside
            className="flex w-72 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-card"
            style={{ height }}
          >
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-medium">{t("promptEditor.placeholders")}</span>
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                aria-label={t("promptEditor.closePlaceholders")}
                className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="border-b p-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={sidebarFilter}
                  onChange={(e) => setSidebarFilter(e.target.value)}
                  placeholder={t("promptEditor.filterPlaceholder")}
                  className="h-8 pl-7 text-xs"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {groups.length === 0 ? (
                <p className="px-1 py-2 text-xs text-muted-foreground">
                  {t("promptEditor.noMatches")}
                </p>
              ) : (
                groups.map((group) => (
                  <div key={group.category} className="mb-3 last:mb-0">
                    <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.category}
                    </p>
                    <ul className="space-y-0.5">
                      {group.items.map((item) => (
                        <li key={item.name}>
                          <button
                            type="button"
                            title={t("promptEditor.insertHint", { description: item.description })}
                            // onMouseDown so the textarea keeps its caret.
                            onMouseDown={(e) => {
                              e.preventDefault();
                              insertAtCaret(item.name);
                            }}
                            className="flex w-full flex-col rounded px-2 py-1 text-left hover:bg-muted"
                          >
                            <span className="font-mono text-xs">{`{{${item.name}}}`}</span>
                            <span className="truncate text-[11px] text-muted-foreground">
                              {item.description}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              )}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
